import React, {
  memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import Tooltip from '../Tooltip';
import { COLOR_LABELS, getContrastTextColor } from '../../helpers/colorsHelper';
import Logger from '../../services/Logger';
import { changeColor } from '../../store/actions/assets';
import { selectColorOptions } from '../../store/selectors/colorLabels';
import ColorLabelSettingsButton from '../colorLabelSettings/ColorLabelSettingsButton';
import makeTooltipText from './helpers/tooltipColorText';
import shouldDropColorsUp from './helpers/shouldDropColorsUp';

const COLORS_OPEN_EVENT = 'catalogItem:colors:open';
let colorsInstanceSeq = 0;

function Colors({
  color, assetId, disabled, colorAt, colorBy, isListViewMode = false,
}) {
  const dispatch = useDispatch();
  const colors = useSelector(selectColorOptions);
  const userId = useSelector((state) => state.user._id);
  const instanceId = useRef(`colors-${colorsInstanceSeq += 1}`).current;
  const wrapperRef = useRef(null);
  const listRef = useRef(null);
  const [showDrop, setShowDrop] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const selectedColor = useMemo(
    () => colors.find((colorItem) => colorItem.value === color)
      || colors.find((colorItem) => colorItem.value === COLOR_LABELS.noColor),
    [color, colors],
  );

  const closeDrop = useCallback((loggerText = 'Close') => {
    setShowDrop((isOpen) => {
      if (isOpen) Logger.log('User', `ThumbnailColors${loggerText}`);
      return false;
    });
  }, []);

  const handleClick = useCallback(
    () => {
      if (disabled) return;
      if (showDrop) {
        if (!isListViewMode) {
          Logger.log('User', 'ThumbnailColorsClose');
          setShowDrop(false);
        }
        return;
      }
      Logger.log('User', 'ThumbnailColorsOpen');
      window.dispatchEvent(new CustomEvent(COLORS_OPEN_EVENT, { detail: { instanceId } }));
      setShowDrop(true);
    },
    [disabled, instanceId, isListViewMode, showDrop],
  );

  const handlePointerLeave = useCallback(() => {
    if (isListViewMode) closeDrop();
  }, [closeDrop, isListViewMode]);

  const handleChange = useCallback(
    (event) => {
      const value = event.currentTarget.dataset.color || COLOR_LABELS.noColor;
      if (!disabled) {
        if (value === COLOR_LABELS.noColor) {
          Logger.log('User', 'MainThumbnailColorsNoneClicked', {
            previousLabelId: color,
            userId,
          });
        } else {
          const label = colors.find((colorItem) => colorItem.value === value);
          Logger.log('User', 'MainThumbnailColorChanged', {
            labelId: value,
            isCustomNamed: label?.name !== value,
            userId,
          });
        }
        dispatch(changeColor([assetId], value));
      }
      setShowDrop(false);
    },
    [assetId, color, colors, disabled, dispatch, userId],
  );

  useEffect(() => {
    const handleOtherOpen = (event) => {
      if (event.detail?.instanceId !== instanceId) closeDrop();
    };

    window.addEventListener(COLORS_OPEN_EVENT, handleOtherOpen);
    return () => window.removeEventListener(COLORS_OPEN_EVENT, handleOtherOpen);
  }, [closeDrop, instanceId]);

  useEffect(() => {
    if (!showDrop || !isListViewMode) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeDrop();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeDrop, isListViewMode, showDrop]);

  useLayoutEffect(() => {
    if (!showDrop || !isListViewMode) {
      setDropUp(false);
      return;
    }
    if (!wrapperRef.current || !listRef.current) return;

    const container = wrapperRef.current.closest('.innerCatalog');
    setDropUp(shouldDropColorsUp({
      openerRect: wrapperRef.current.getBoundingClientRect(),
      containerRect: container
        ? container.getBoundingClientRect()
        : { top: 0, bottom: window.innerHeight },
      listHeight: listRef.current.offsetHeight,
    }));
  }, [isListViewMode, showDrop]);

  const tooltipText = useMemo(
    () => makeTooltipText(selectedColor.title || color, colorAt, colorBy),
    [color, selectedColor.title, colorAt, colorBy],
  );

  const { text, ...buttonStyles } = useMemo(
    () => ({
      text: selectedColor.value === COLOR_LABELS.noColor ? 'color' : selectedColor.name,
      background: selectedColor.background || 'none',
      color: getContrastTextColor(selectedColor.background),
    }),
    [selectedColor.background, selectedColor.name, selectedColor.value],
  );

  return (
    <div
      ref={wrapperRef}
      className={cn('catalogItem__colors', { isOpen: showDrop, dropUp })}
      onMouseLeave={handlePointerLeave}
    >
      <Tooltip content={tooltipText} placement={isListViewMode ? 'top' : 'left'}>
        <span
          className={cn('catalogItem__colors-opener', {
            noColor: selectedColor.value === COLOR_LABELS.noColor,
          })}
          role="button"
          tabIndex={0}
          aria-label={text}
          onClick={handleClick}
          onKeyPress={handleClick}
          style={buttonStyles}
        >
          <span className="catalogItem__colors-text">
            {text}
          </span>
        </span>
      </Tooltip>
      <If condition={showDrop}>
        <div ref={listRef} className="catalogItem__colors-list">
          {colors.map((colorItem) => (
            <Tooltip content={colorItem.title} placement="left" key={colorItem.value}>
              <div
                className={cn('catalogItem__colors-color', { act: colorItem.value === color })}
                tabIndex={0}
                role="button"
                aria-label={colorItem.title}
                style={{ background: colorItem.background }}
                data-color={colorItem.value}
                onClick={handleChange}
                onKeyPress={handleChange}
              >
                <If condition={colorItem.value === COLOR_LABELS.noColor}>none</If>
              </div>
            </Tooltip>
          ))}
          <ColorLabelSettingsButton
            className="catalogItem__colors-settings"
            loggerEvent="MainThumbnailColorsEditLabelsClicked"
          />
        </div>
      </If>
    </div>
  );
}

Colors.propTypes = {
  assetId: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  colorAt: PropTypes.string,
  colorBy: PropTypes.shape({
    _id: PropTypes.string,
    displayName: PropTypes.string,
    email: PropTypes.string,
  }),
  isListViewMode: PropTypes.bool,
};

export default memo(Colors);
